"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef } from "react";
import { preload } from "react-dom";
import { useAgentStore } from "@/features/agent/store/agentStore";
import { useNearViewport } from "@/lib/hooks/useNearViewport";
import { useHlsPlayback } from "@/lib/video/useHlsPlayback";
import { usePauseWhenInvisible } from "@/lib/video/usePauseWhenInvisible";
import type { VideoOptions, VideoProps } from "@/types";
export interface VideoElementProps {
  video: VideoProps;
  /**
   * For videos above the fold: skips the wait for the viewport and preloads
   * the poster.
   */
  priority?: boolean;
  className?: string;
}

/**
 * The poster is part of the server-rendered markup and the source is not, so a
 * video far down the page costs nothing until it is near the viewport.
 * Priority videos without a transcoded version keep their source in the
 * markup, as they did before.
 *
 * An autoplaying video is also paused again whenever it leaves the viewport or
 * its tab goes to the background (`usePauseWhenInvisible`).
 */
export const Video: React.FC<VideoElementProps> = t0 => {
  const $ = _c(21);
  const {
    video,
    priority: t1,
    className
  } = t0;
  const priority = t1 === undefined ? false : t1;
  const elementRef = useRef(null);
  let t2;
  if ($[0] !== priority) {
    t2 = {
      skip: priority
    };
    $[0] = priority;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isActive = useNearViewport(elementRef, t2);
  let t3;
  if ($[2] !== video.options) {
    t3 = resolveVideoOptions(video.options);
    $[2] = video.options;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    autoplay,
    controls,
    loop,
    muted
  } = t3;
  useHlsPlayback(elementRef, video, isActive);
  usePauseWhenInvisible(elementRef, autoplay);
  const isAgentOpen = useAgentStore().isOpen;
  let t4;
  let t5;
  if ($[4] !== autoplay || $[5] !== isAgentOpen) {
    t4 = () => {
      const element = elementRef.current;
      if (!autoplay || !element) {
        return;
      }
      let hasAutoPaused = false;
      const syncPlayback = () => {
        if (isAgentOpen) {
          if (element.paused) {
            return;
          }
          hasAutoPaused = true;
          element.pause();
        } else {
          if (hasAutoPaused) {
            hasAutoPaused = false;
            element.play().catch(_temp);
          }
        }
      };
      syncPlayback();
      element.addEventListener("play", syncPlayback);
      return () => {
        element.removeEventListener("play", syncPlayback);
        if (hasAutoPaused && element.isConnected) {
          element.play().catch(_temp2);
        }
      };
    };
    t5 = [autoplay, isAgentOpen];
    $[4] = autoplay;
    $[5] = isAgentOpen;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  if (priority && video.poster) {
    preload(video.poster, {
      as: "image",
      fetchPriority: "high"
    });
  }
  const t6 = isActive ? undefined : "none";
  let t7;
  if ($[8] !== isActive || $[9] !== video.hlsSrc || $[10] !== video.src) {
    t7 = isActive && !video.hlsSrc && <source src={video.src} type="video/mp4" />;
    $[8] = isActive;
    $[9] = video.hlsSrc;
    $[10] = video.src;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== autoplay || $[13] !== className || $[14] !== controls || $[15] !== loop || $[16] !== muted || $[17] !== t6 || $[18] !== t7 || $[19] !== video.poster) {
    t8 = <video ref={elementRef} poster={video.poster} preload={t6} autoPlay={autoplay} muted={muted} loop={loop} controls={controls} playsInline={true} className={className}>{t7}</video>;
    $[12] = autoplay;
    $[13] = className;
    $[14] = controls;
    $[15] = loop;
    $[16] = muted;
    $[17] = t6;
    $[18] = t7;
    $[19] = video.poster;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
};
function resolveVideoOptions(options?: VideoOptions): Required<VideoOptions> {
  return {
    autoplay: options?.autoplay ?? true,
    controls: options?.controls ?? false,
    loop: options?.loop ?? true,
    muted: options?.muted ?? true
  };
}
function _temp() {}
function _temp2() {}